import { useQuery, UseQueryResult } from '@tanstack/react-query'
import {
    IEntityInstanceRelationsResponse,
    IMulitEntityInstanceRelationsResponse,
    RelatedEntityInstance,
} from '../../services/apiInterfaces'
import {
    fetchMultiRecordRelationsByArrayOfIDs,
    fetchRecordRelationDataByID,
} from '../../services/entityServices'
import { relationStatus } from '../../constants/constants'
import { getNearestRelationNameByTargetEntityTemplateId } from '../../utils/RelationUtils'
import { IAssetSensorMarker } from '../../components/Applications/FacilityManagement/interfaces'
import { getAttributeById } from '../../utils/AttributeUtils'
import { useAppSelector } from '../../redux/hook'
import { IAttribute } from '../../components/DTStudio/interfaces'

export interface IAssetsAndSensorsInBuilding {
    previouslyConfiguredSensorMarkers: IAssetSensorMarker[]
    allAssetSensors: IAsset[]
}

export interface IAsset {
    uid: string
    entityInstanceId: string
    attributes: IAttribute[]
    displayName: string
    relatedSensor: RelatedEntityInstance | undefined
}

export const useGetAssetsAndSensorsInBuilding = (): UseQueryResult<
    IAssetsAndSensorsInBuilding,
    Error
> => {
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const {
        selectedBuilding,
        buildingEntityTemplateId,
        assetEntityTemplateId,
        sensorEntityTemplateId,
        assetXCoordinateAttributeId,
        assetYCoordinateAttributeId,
        buildingRelations,
        assetRelations,
    } = fmSlice
    const navList = useAppSelector((state) => state.nav.navList)
    const buildingInstanceId = selectedBuilding?.entityInstanceId
    const orgId = useAppSelector((state) => state.org.org?.id)
    const user = useAppSelector((state) => state.user.user)
    return useQuery<IAssetsAndSensorsInBuilding>({
        queryKey: [
            'assetsAndSensorInBuilding',
            buildingInstanceId,
            buildingEntityTemplateId,
            orgId,
            user,
        ],
        queryFn: async () => {
            const buildingRelationRecords: IEntityInstanceRelationsResponse =
                await fetchRecordRelationDataByID(
                    orgId || '',
                    buildingEntityTemplateId,
                    buildingInstanceId,
                )
            const buildingAssetRelation =
                buildingRelations.find(
                    (relation) =>
                        relation.childEntity === assetEntityTemplateId,
                )?.relationName || ''

            const activeAssets: RelatedEntityInstance[] =
                buildingRelationRecords?.relations?.[
                    buildingAssetRelation
                ]?.filter(
                    (entityInstance) =>
                        entityInstance.status === relationStatus.ACTIVE,
                )
            if (!activeAssets || activeAssets?.length === 0) {
                return {
                    previouslyConfiguredSensorMarkers: [],
                    allAssetSensors: [],
                }
            }

            const assetIds = activeAssets?.map(
                (asset) => asset.entityInstanceId,
            )
            const allAssetsRelationsRecords: IMulitEntityInstanceRelationsResponse =
                await fetchMultiRecordRelationsByArrayOfIDs(
                    orgId || '',
                    assetEntityTemplateId,
                    assetIds.join(','),
                    false,
                )
            const assetSensorRelationName =
                getNearestRelationNameByTargetEntityTemplateId(
                    assetRelations,
                    sensorEntityTemplateId,
                    navList,
                )
            const allAssetSensors: IAsset[] = []
            const previousSensorMarkers: IAssetSensorMarker[] = activeAssets
                .map((asset) => {
                    const assetId = asset.entityInstanceId
                    const assetName: string = asset.uid || ''

                    /**
                     * Asset to Sensor is always one to one relation. We assume there will be only one active
                     * sensor attached to an asset. so here we are looking for the first active sensor attached to an asset.
                     */
                    const sensor = allAssetsRelationsRecords[
                        assetId
                    ]?.relations[assetSensorRelationName]?.find(
                        (sensor) => sensor.status === relationStatus.ACTIVE,
                    )

                    const assetSensor: IAsset = {
                        uid: asset.uid,
                        entityInstanceId: asset.entityInstanceId,
                        attributes: asset.attributes,
                        displayName: asset.displayName,
                        relatedSensor: sensor,
                    }
                    allAssetSensors.push(assetSensor)
                    const x =
                        Number(
                            getAttributeById(
                                asset.attributes,
                                assetXCoordinateAttributeId,
                            )?.value,
                        ) || 0
                    const y =
                        Number(
                            getAttributeById(
                                asset.attributes,
                                assetYCoordinateAttributeId,
                            )?.value,
                        ) || 0
                    if (!sensor || (!x && !y)) {
                        return undefined
                    }

                    return {
                        alarmStatus: asset.alarmStatus,
                        assetInstanceId: assetId,
                        assetName,
                        sensor,
                        sensorId: sensor.entityInstanceId,
                        x,
                        y,
                    } as IAssetSensorMarker
                })
                .filter(
                    (marker): marker is IAssetSensorMarker =>
                        marker !== undefined,
                )

            allAssetSensors.sort((a, b) => {
                // First sort by sensor presence (null sensors first)
                if (
                    (!a.relatedSensor && b.relatedSensor) ||
                    (a.relatedSensor && !b.relatedSensor)
                ) {
                    return a.relatedSensor ? 1 : -1
                }
                const isPlacedOnLayout = (assetAttributes: IAttribute[]) => {
                    const x =
                        Number(
                            getAttributeById(
                                assetAttributes,
                                assetXCoordinateAttributeId,
                            )?.value,
                        ) || 0
                    const y =
                        Number(
                            getAttributeById(
                                assetAttributes,
                                assetYCoordinateAttributeId,
                            )?.value,
                        ) || 0
                    return !!x || !!y
                }
                //Then sorty by displaying uncofigured assetsSensors first and configured assetsSensors later
                if (
                    isPlacedOnLayout(a.attributes) &&
                    !isPlacedOnLayout(b.attributes)
                ) {
                    return 1
                } else if (
                    !isPlacedOnLayout(a.attributes) &&
                    isPlacedOnLayout(b.attributes)
                ) {
                    return -1
                }
                // Then sort by displayName
                return a.displayName.localeCompare(b.displayName)
            })
            return {
                previouslyConfiguredSensorMarkers: previousSensorMarkers,
                allAssetSensors,
            }
        },
        refetchOnWindowFocus: false,
        enabled: !!buildingInstanceId && !!buildingEntityTemplateId,
    })
}
